import React, { forwardRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertTriangle, Lock, RefreshCw, SearchX, CalendarOff } from 'lucide-react';

/** Én flate per seksjon. Underinnhold bruker hairlines/inset – ikke kort i kort. */
export const Panel = forwardRef(function Panel({ className, children, as: Tag = 'section', ...props }, ref) {
  return (
    <Tag ref={ref} className={cn('hr-surface', className)} {...props}>
      {children}
    </Tag>
  );
});

/** Paneltopp: tittel 16/24 (H2), beskrivelse 14/20, handlinger til høyre. 8 px-rytme: px-4/5, py-3. */
export function PanelHeader({ title, description, actions, eyebrow, className, titleAs: TitleTag = 'h2', titleTestId }) {
  return (
    <div className={cn('flex flex-col gap-3 border-b border-[var(--hr-hairline)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5', className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-0.5 text-[12px] font-semibold uppercase leading-4 tracking-[0.08em] text-[var(--hr-muted-2)]">{eyebrow}</div>}
        <TitleTag className="text-[16px] font-semibold leading-6 tracking-[-0.01em] text-[var(--hr-heading)]" data-testid={titleTestId}>{title}</TitleTag>
        {description && <p className="mt-0.5 max-w-3xl break-words text-sm leading-5 text-[var(--hr-muted)] [overflow-wrap:anywhere]">{description}</p>}
      </div>
      {actions && <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2 sm:max-w-[60%] sm:justify-end">{actions}</div>}
    </div>
  );
}

export function PanelBody({ className, children, ...props }) {
  return (
    <div className={cn('px-4 py-4 sm:px-5', className)} {...props}>
      {children}
    </div>
  );
}

export function PanelFooter({ className, children, ...props }) {
  return (
    <div className={cn('border-t border-[var(--hr-hairline)] px-4 py-3 text-sm leading-5 text-[var(--hr-muted)] sm:px-5', className)} {...props}>
      {children}
    </div>
  );
}

export function Inset({ className, children, ...props }) {
  return (
    <div className={cn('hr-inset px-4 py-3', className)} {...props}>
      {children}
    </div>
  );
}

export function SkeletonKpis({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4" aria-busy="true" aria-label="Laster nøkkeltall">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="hr-surface space-y-3 px-5 py-5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-3 w-40" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 6 }) {
  return (
    <Panel className="p-5" aria-busy="true" aria-label="Laster tabell">
      <Skeleton className="mb-5 h-4 w-48" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    </Panel>
  );
}

export function SkeletonChart() {
  return (
    <Panel className="p-5" aria-busy="true" aria-label="Laster graf">
      <Skeleton className="mb-5 h-4 w-56" />
      <Skeleton className="h-[240px] w-full" />
    </Panel>
  );
}

export function PageSkeleton() {
  return (
    <div className="space-y-6" data-testid="page-skeleton">
      <div className="space-y-2">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-4 w-[520px] max-w-full" />
      </div>
      <SkeletonKpis />
      <SkeletonChart />
      <SkeletonTable />
    </div>
  );
}

export function EmptyState({ type = 'no_results', title, description, onReset, resetLabel = 'Nullstill filtre', action, className }) {
  const Icon = type === 'planned_off' ? CalendarOff : type === 'no_access' ? Lock : type === 'error' ? AlertTriangle : SearchX;
  return (
    <div data-testid={`empty-state-${type}`} className={cn('flex flex-col items-center gap-4 rounded-[var(--hr-radius)] border border-dashed border-[var(--hr-border)] bg-[var(--hr-panel)] px-6 py-12 text-center', className)}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--hr-inset)] text-[var(--hr-muted)]">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <h3 className="text-[16px] font-semibold leading-6 text-[var(--hr-heading)]">{title}</h3>
        {description && <p className="mx-auto mt-1 max-w-prose text-sm leading-5 text-[var(--hr-muted)]">{description}</p>}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {onReset && (
          <Button variant="outline" size="sm" onClick={onReset} data-testid="empty-state-reset-button">
            {resetLabel}
          </Button>
        )}
        {action}
      </div>
    </div>
  );
}

export function ErrorState({ title = 'Kunne ikke hente data', description, source, onRetry }) {
  return (
    <div data-testid="error-state" role="alert" className="flex flex-col items-start gap-4 rounded-[var(--hr-radius)] border border-[rgba(180,35,24,0.25)] bg-[var(--hr-red-bg)] px-6 py-6">
      <div className="flex items-center gap-2 text-[var(--hr-red)]">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        <h3 className="font-display text-[18px] font-semibold leading-6 text-[var(--hr-red)]">{title}</h3>
      </div>
      {source && <p className="text-sm leading-5 text-[var(--hr-text)]">Kilde: {source}</p>}
      {description && <p className="max-w-prose text-[15px] leading-6 text-[var(--hr-text)]">{description}</p>}
      <p className="text-sm leading-5 text-[var(--hr-muted)]">Ingen automatiske endringer er gjort i grunnlagsdata.</p>
      {onRetry && (
        <Button size="sm" onClick={onRetry} data-testid="error-retry-button">
          <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" /> Prøv igjen
        </Button>
      )}
    </div>
  );
}

export function NoAccess({ roleLabel, what = 'denne visningen', children }) {
  return (
    <EmptyState
      type="no_access"
      title="Rollen mangler tilgang"
      description={`Rollen ${roleLabel} har ikke tilgang til ${what}. Bytt rolle i profilmenyen for å se den. Skjulte verdier tas heller ikke med i eksport.`}
      action={children}
    />
  );
}
